// Nurse3PatientQueue.tsx
// import React, { useEffect, useMemo, useState } from "react";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "../../../api/axiosConfig";
import "../../../center admin css/centerAdmin.css";

/** ====== Center Workflow response types ====== */
type WaitingItem = {
  _id: string;
  appointmentId?: string;
  stationId: string;
  sessionId?: string;
  createdAt: string;
  state?: string;
  startedAt?: string;
  child: {
    childId: string;
    name: string;
    age: number;
    gender: string;
    emrId: string;
    profile?: string;
    profileKey?: string;
  };
  pathId?: string;
  sessionStartAt?: string;
};

type StationPatientsResp = {
  waiting: WaitingItem[];
  inProgress: WaitingItem[];
  done?: WaitingItem[];
};

type QueueItem = {
  rowIndex: number;
  id: string;
  date: string;
  name: string;
  genderShort: "M" | "F";
  ageYears: number;
  avatarUrl?: string;
  profileKey?: string;
  emrId: string; // ✅ Required for map lookup
  time?: string;
  

  _source?: "IN_QUEUE" | "IN_PROGRESS" | "WAITING" | "NO_CHILD";
  state?: "IN_QUEUE" | "IN_PROGRESS" | "WAITING" | "NO_CHILD";
  isEmpty?: boolean;
  slotLabel?: string;
  sessionId?: string;
  pathOrder?: number;
};

type Props = {
  centerId: string;
  dateISO?: string;
  activeId?: string;

  onSelect?: (item: WaitingItem) => void;
  autoRefreshMs?: number;
  stationId?: string;
};

function formatDmy(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

function formatHm(iso?: string) {
  if (!iso) return "";
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function todayISO() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}

const Nurse3PatientQueue: React.FC<Props> = ({
  centerId,
  dateISO,
  activeId,
  onSelect,
  autoRefreshMs,
  stationId = "NURSE3",
}) => {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [items, setItems] = useState<QueueItem[]>([]);
  const [rawInProgress, setRawInProgress] = useState<WaitingItem[]>([]);
  const [rawInQueue, setRawInQueue] = useState<WaitingItem[]>([]);
  // const [rawInProgress, setRawInProgress] = useState<WaitingItem[]>([]);
  const [avatarMap, setAvatarMap] = useState<Record<string, string>>({});

  const effectiveDate = dateISO || todayISO();

  const getCenterId = () => {
    try {
      const rawConfig = sessionStorage.getItem("configResponse");

      if (!rawConfig) return "";

      const parsedConfig = JSON.parse(rawConfig);

      return parsedConfig?.data?.[0]?.centerId || "";
    } catch (error) {
      console.error("Failed to read configResponse:", error);
      return "";
    }
  };

  /** ── queue loader ── */
  // async function load() {
  const load = useCallback(async () => {
    if (items.length === 0) setLoading(true);
    setErr(null);

    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) throw new Error("No access token found.");

      const headers = { Authorization: `Bearer ${token}` };

      const currentCenterId = getCenterId();

      const { data } = await api.centerworkflowApi.get(
        `/queues/NURSE3_Q?center_id=${encodeURIComponent(
          currentCenterId,
        )}&date=${encodeURIComponent(effectiveDate)}`,
        { headers },
      );

      const matrixRows = Array.isArray(data?.matrixRows) ? data.matrixRows : [];

      const inQueue = Array.isArray(data?.inQueue) ? data.inQueue : [];
      const inProgress = Array.isArray(data?.inProgress) ? data.inProgress : [];

      setRawInQueue(inQueue);
      setRawInProgress(inProgress);
      const mapped: QueueItem[] = matrixRows
        .filter(
          (row: any) =>
            row.state === "IN_QUEUE" ||
            row.state === "IN_PROGRESS" ||
            row.state === "WAITING" ||
            row.state === "NO_CHILD",
        )
        .map((row: any) => {
          const item = row.item;

          const ts = item?.startedAt || item?.sessionStartAt || item?.createdAt;

          return {
            id: item?._id || `empty-${row.row}`,

            emrId: row.child?.childId || "",

            date: formatDmy(ts),

            time: formatHm(ts),

            // slotLabel: row.slotLabel || row.slot_label || "",
            slotLabel:
              row?.scheduledTime?.label ||
              row?.item?.scheduledTime?.label ||
              row?.slotLabel ||
              row?.slot_label ||
              "",

            name:
              row.state === "NO_CHILD"
                ? "No Patient"
                : row.child?.name || "Unknown",

            genderShort: String(row.child?.gender || "M")
              .toUpperCase()
              .startsWith("F")
              ? "F"
              : "M",

            ageYears: Number(row.child?.age ?? 0),

            avatarUrl: undefined,

            profileKey: row.child?.profile,

            _source: row.state,

            state: row.state,

            isEmpty: row.state === "NO_CHILD",

            sessionId: row.sessionId,

            // pathOrder: row.pathOrder || 0,
            pathOrder: row.item?.pathOrder || 0,

            rowIndex: row.row || 0,
          };
        });

      // setItems(mapped);
      const sortedMapped = [...mapped].sort(
        (a, b) => (a.rowIndex || 0) - (b.rowIndex || 0),
      );

      setItems(sortedMapped);

      // Auto-select only if there is no active patient AND there is a valid queue item.
      // const hasActive = activeId && mapped.some((m) => m.id === activeId);
      console.log("activeId =", activeId);

      console.log(
        "mapped ids =",
        mapped.map((m) => ({
          id: m.id,
          name: m.name,
        })),
      );

      const hasActive = activeId && mapped.some((m) => m.id === activeId);

      console.log("hasActive =", hasActive);

      if (!hasActive) {
        // const firstInQueue = inQueue.find((x: any) => x.state === "IN_QUEUE");

        const activePatient =
          inProgress[0] || inQueue.find((x: any) => x.state === "IN_QUEUE");

        if (activePatient) {
          sessionStorage.setItem(
            "selectedPatient_NURSE3_Q",
            JSON.stringify(activePatient),
          );

          sessionStorage.setItem("activeNurse3PatientId", activePatient._id);

          console.log(
            "AUTO SELECT FROM LOAD",
            activePatient.child?.name,
            activePatient._id,
          );

          onSelect?.(activePatient);
        } else {
          // Queue is empty after Complete.
          sessionStorage.removeItem("selectedPatient_NURSE3_Q");
          sessionStorage.removeItem("activeNurse3PatientId");

          onSelect?.(null as any);
        }
      }

      // 2. Fetch Images (Using exact Nurse 1 logic)
      await fetchImages(mapped, headers);
    } catch (e: any) {
      setErr(e?.message || "Failed to fetch station queue");
    } finally {
      setLoading(false);
    }
  }, [activeId, effectiveDate, items.length, onSelect]);

  /* ------------------ 🔐 PRESIGNED IMAGE FETCH (EXACT NURSE 1 LOGIC) ------------------ */
  // async function fetchImages(queueItems: QueueItem[], headers: any) {
  //   const currentCenterId = getCenterId();
  //   if (!currentCenterId) return;

  //   try {
  //     const apptRes = await api.appointment_api.get(
  //       `/api/appointments/center/${encodeURIComponent(
  //         currentCenterId,
  //       )}/date?date=${encodeURIComponent(effectiveDate)}`,
  //       { headers },
  //     );

  //     const slots = apptRes.data?.slots || [];

  //     const bookingSlotMap: Record<string, string> = {};

  //     slots.forEach((slot: any) => {
  //       const slotLabel = slot.slot_label;

  //       (slot.bookings || []).forEach((booking: any) => {
  //         if (booking.child_id) {
  //           bookingSlotMap[booking.child_id] = slotLabel;
  //         }
  //       });
  //     });

  //     const bookings = slots.flatMap((slot: any) => slot.bookings || []);

  //     const newMap: Record<string, string> = {};

  //     for (const b of bookings) {
  //       const filePath = b.child_profile;
  //       if (!filePath || !b.child_id) continue;

  //       // Skip if we already have this image
  //       if (avatarMap[b.child_id]) {
  //         newMap[b.child_id] = avatarMap[b.child_id];
  //         continue;
  //       }

  //       try {
  //         // If already presigned, use as is
  //         if (filePath.includes("X-Amz-Signature")) {
  //           newMap[b.child_id] = filePath;
  //           continue;
  //         }

  //         // ✅ FIX: Manual string splitting to match Nurse 1 logic
  //         // This generates "children/profile-photo" and "P-2025..." correctly
  //         // ✅ Remove leading slash if present
  //         const cleanPath = filePath.startsWith("/")
  //           ? filePath.slice(1)
  //           : filePath;

  //         const parts = cleanPath.split("/");

  //         // Safety check
  //         if (parts.length < 3) continue;

  //         // Correct extraction
  //         const module = `${parts[0]}/${parts[1]}`;
  //         const key = parts.slice(2).join("/");

  //         const img = await api.S3config.get("/view-url", {
  //           params: { module, key },
  //           headers,
  //         });

  //         // Robust response check
  //         const signedUrl = img.data?.url || (img as any).url;
  //         if (signedUrl) {
  //           newMap[b.child_id] = signedUrl;
  //         }
  //       } catch (e) {
  //         // ignore failures
  //       }
  //     }

  //     setAvatarMap((prev) => ({ ...prev, ...newMap }));

  //     setItems((prev) =>
  //       [...prev].sort((a, b) => (a.rowIndex || 0) - (b.rowIndex || 0)),
  //     );
  //   } catch (e) {
  //     console.warn("Avatar load failed", e);
  //   }
  // }

  async function fetchImages(queueItems: QueueItem[], headers: any) {
    try {
      const newMap: Record<string, string> = {};

      for (const item of queueItems) {
        const filePath = item.profileKey;

        if (!filePath || !item.emrId) continue;

        // Reuse already resolved image
        if (avatarMap[item.emrId]) {
          newMap[item.emrId] = avatarMap[item.emrId];
          continue;
        }

        try {
          // Already a presigned URL
          if (filePath.includes("X-Amz-Signature")) {
            newMap[item.emrId] = filePath;
            continue;
          }

          // Remove leading slash if present
          const cleanPath = filePath.startsWith("/")
            ? filePath.slice(1)
            : filePath;

          const parts = cleanPath.split("/");

          // Expected:
          // children/profile-photo/<key>
          if (parts.length < 3) {
            console.warn("Invalid profile key:", filePath);
            continue;
          }

          const module = `${parts[0]}/${parts[1]}`;
          const key = parts.slice(2).join("/");

          const img = await api.S3config.get("/view-url", {
            params: {
              module,
              key,
            },
            headers,
          });

          const signedUrl = img.data?.url || (img as any).url;

          if (signedUrl) {
            newMap[item.emrId] = signedUrl;
          }
        } catch (error) {
          console.warn(`Failed to generate image URL for ${item.emrId}`, error);
        }
      }

      setAvatarMap((prev) => ({
        ...prev,
        ...newMap,
      }));
    } catch (error) {
      console.warn("Nurse3 avatar load failed", error);
    }
  }

  /* --------------------------------------------------------------------------------- */

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, stationId]);

  useEffect(() => {
    const handler = () => {
      load();
    };

    window.addEventListener("patientUpdated", handler);

    return () => {
      window.removeEventListener("patientUpdated", handler);
    };
  }, [load]);

  useEffect(() => {
    if (!autoRefreshMs) return;

    const t = setInterval(load, autoRefreshMs);

    return () => clearInterval(t);
  }, [load, stationId, autoRefreshMs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();

    // Map images to items
    const withImages = items.map((i) => ({
      ...i,
      avatarUrl:
        avatarMap[i.emrId] || avatarMap[i.emrId?.trim()] || i.avatarUrl,
    }));

    if (!q) return withImages;
    return withImages.filter((p) => p.name.toLowerCase().includes(q));
  }, [items, query, avatarMap]);

  return (
    <aside className="gc-queue">
      <div className="gc-queue-header">
        <div className="gc-queue-title">
          Patient Queue
          <span className="gc-queue-underline" />
        </div>

        <div className="gc-queue-searchwrap">
          <input
            className="gc-queue-search"
            placeholder="Search patients..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="gc-queue-list">
        {loading && items.length === 0 && (
          <div className="gc-queue-empty">Loading…</div>
        )}
        {err && items.length === 0 && (
          <div className="gc-queue-empty">Error: {err}</div>
        )}
        {!loading && !err && filtered.length === 0 && (
          <div className="gc-queue-empty">No patients</div>
        )}

        {filtered.map((p) => {
          const active = p.id === activeId;
          return (
            <button
              key={p.id}
              className={`gc-queue-item ${active ? "active" : ""} ${
                p._source === "IN_QUEUE" || p._source === "IN_PROGRESS"
                  ? "in-progress"
                  : p._source === "NO_CHILD"
                    ? "no-child"
                    : "waiting"
              }`}
              onClick={() => {
                if (p._source === "IN_QUEUE" || p._source === "IN_PROGRESS") {
                  const selectedPatient =
                    rawInQueue.find((x) => x._id === p.id) ||
                    rawInProgress.find((x) => x._id === p.id);

                  if (selectedPatient) {
                    sessionStorage.setItem(
                      "selectedPatient_NURSE3_Q",
                      JSON.stringify(selectedPatient),
                    );

                    sessionStorage.setItem(
                      "activeNurse3PatientId",
                      selectedPatient._id,
                    );

                    onSelect?.(selectedPatient);
                    console.log("QUEUE CLICK", selectedPatient);

                    window.dispatchEvent(new Event("patientUpdated"));
                  }
                }
              }}
              disabled={p._source !== "IN_QUEUE" && p._source !== "IN_PROGRESS"}
              style={{
                // cursor: p._source === "WAITING" ? "pointer" : "not-allowed",
                cursor:
                  p._source === "IN_QUEUE" || p._source === "IN_PROGRESS"
                    ? "pointer"
                    : "not-allowed",
                // opacity: p._source === "WAITING" ? 1 : 0.6,
                opacity:
                  p._source === "IN_QUEUE" || p._source === "IN_PROGRESS"
                    ? 1
                    : 0.6,
              }}
              title={p.time ? `Time: ${p.time}` : undefined}
            >
              <div className="gc-queue-item-body">
                <div className="gc-queue-avatar">
                  {p.avatarUrl ? (
                    <img src={p.avatarUrl} alt={p.name} />
                  ) : (
                    <div className="gc-queue-avatar-fallback" />
                  )}
                </div>

                <div className="gc-queue-item-info">
                  <div className="gc-queue-item-date">
                    {p.slotLabel ? `${p.slotLabel} · ${p.date}` : p.date}
                  </div>

                  <div className="gc-queue-name">
                    {p.isEmpty ? "No Child" : p.name}
                  </div>

                  <div className="gc-queue-sub">
                    {p.isEmpty
                      ? "Waiting for Child"
                      : `${p.genderShort}, ${p.ageYears} ${
                          p.ageYears === 1 ? "Year" : "Years"
                        }`}
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="gc-queue-footer">
        <div className="gc-queue-meta">
          <small>
            Station: <b>{stationId}</b> · Date: <b>{effectiveDate}</b>
          </small>
        </div>
      </div>
    </aside>
  );
};

export default Nurse3PatientQueue;
